export const OPTION_METAL = 'metal';
export const OPTION_SIZE = 'size';

export function optionKey(name) {
  return String(name).trim().toLowerCase();
}

export function parseOptionValue(value) {
  const text = String(value);
  const match = /^(.*?)\s*\((.+)\)\s*$/.exec(text);
  return match ? { primary: match[2], secondary: match[1] } : { primary: text, secondary: '' };
}

export function variantValue(offer, variant, key) {
  const index = offer.options.findIndex((name) => optionKey(name) === key);
  return index === -1 ? null : variant.options[index];
}

export function findVariant(offer, selections) {
  let best = null;
  let bestScore = -1;
  for (const variant of offer.variants) {
    const matches = offer.options.filter((name, index) => selections[optionKey(name)] === variant.options[index]).length;
    // An exact match gets a higher score than availability. This keeps a sold-out choice selected.
    const score = matches * 2 + (variant.available ? 1 : 0);
    if (score > bestScore) {
      best = variant;
      bestScore = score;
    }
  }
  return best;
}

export function applyVariant(offer, variant, selections) {
  const next = { ...selections };
  offer.options.forEach((name, index) => {
    next[optionKey(name)] = variant.options[index];
  });
  return next;
}

export function isValueAvailable(offer, selections, key, value) {
  return offer.variants.some(
    (variant) =>
      variant.available &&
      offer.options.every((name, index) => {
        const optionName = optionKey(name);
        if (optionName === key) return variant.options[index] === value;
        return !(optionName in selections) || variant.options[index] === selections[optionName];
      })
  );
}

export function templateValues(offer, variant) {
  const metal = variantValue(offer, variant, OPTION_METAL);
  const size = variantValue(offer, variant, OPTION_SIZE);
  return {
    price: variant.price,
    metal: metal ? metal.toLowerCase() : '',
    size: size ? (parseOptionValue(size).secondary || size).toLowerCase() : '',
  };
}

export function renderTemplate(template, values) {
  return String(template ?? '').replace(/\[(price|metal|size)\]/g, (token, key) => values[key] ?? token);
}

export function variantUrl(offer, variant) {
  return `${offer.url}?variant=${variant.id}`;
}

function defineElements() {
  class HeirloomGallery extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.show(index)));
    }

    show(index) {
      this.slides.forEach((slide, slideIndex) => {
        slide.hidden = slideIndex !== index;
      });
      this.thumbs.forEach((thumb, thumbIndex) => {
        thumb.setAttribute('aria-current', String(thumbIndex === index));
      });
    }
  }

  class ProductConfigurator extends HTMLElement {
    connectedCallback() {
      this.offers = [...this.querySelectorAll('script[data-configurator-offer]')].map((script) =>
        JSON.parse(script.textContent)
      );
      if (this.offers.length === 0) return;

      this.idInput = this.querySelector('product-form [name="id"]');
      this.submitButton = this.querySelector('product-form [type="submit"]');
      this.offerIndex = Number(this.dataset.selectedOffer) || 0;

      const offer = this.offers[this.offerIndex];
      const variant =
        offer.variants.find((item) => item.id === Number(this.dataset.selectedVariant)) || findVariant(offer, {});
      this.selections = applyVariant(offer, variant, {});

      this.addEventListener('change', this.onChange.bind(this));
      this.render(false);
    }

    onChange(event) {
      const input = event.target;
      if (input.matches('[data-offer-input]')) {
        this.offerIndex = Number(input.value);
      } else if (input.matches('[data-option-input]')) {
        this.selections = { ...this.selections, [input.dataset.optionInput]: input.value };
      } else {
        return;
      }
      this.querySelector('product-form')?.handleErrorMessage();
      this.render(true);
    }

    render(fromUser) {
      const offer = this.offers[this.offerIndex];
      const variant = findVariant(offer, this.selections);
      this.selections = applyVariant(offer, variant, this.selections);

      this.renderOffers();
      this.renderPickers(offer, variant);
      this.renderGalleries();
      this.renderBuyButton(offer, variant);

      if (!fromUser) return;
      history.replaceState(history.state, '', variantUrl(offer, variant));
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId: this.dataset.sectionId, variant } });
      }
    }

    renderOffers() {
      this.offers.forEach((offer, index) => {
        const variant = findVariant(offer, this.selections);
        const input = this.querySelector(`[data-offer-input][value="${index}"]`);
        if (input) input.checked = index === this.offerIndex;
        this.setText(`[data-offer-price="${index}"]`, variant.price);
        this.setCompare(`[data-offer-compare="${index}"]`, variant.compareAtPrice);
        this.setText(
          `[data-offer-description="${index}"]`,
          renderTemplate(offer.templates.description, templateValues(offer, variant))
        );
      });
    }

    renderPickers(offer, variant) {
      this.querySelectorAll('[data-option-picker]').forEach((picker) => {
        const key = picker.dataset.optionPicker;
        picker.hidden = !offer.options.some((name) => optionKey(name) === key);
        if (picker.hidden) return;

        picker.querySelectorAll('[data-option-input]').forEach((input) => {
          const card = input.closest('[data-option-card]');
          const exists = offer.variants.some((item) => variantValue(offer, item, key) === input.value);
          input.checked = this.selections[key] === input.value;
          card.hidden = !exists;
          card.toggleAttribute('data-sold-out', exists && !isValueAvailable(offer, this.selections, key, input.value));
        });

        const note = picker.querySelector('[data-option-note]');
        if (note) note.textContent = picker.dataset.note ? variant[picker.dataset.note] || '' : '';
      });
    }

    renderGalleries() {
      this.querySelectorAll('[data-offer-gallery]').forEach((wrapper) => {
        const active = Number(wrapper.dataset.offerGallery) === this.offerIndex;
        // Show the first slide when a gallery becomes visible again.
        if (active && wrapper.hidden) wrapper.querySelector('heirloom-gallery')?.show(0);
        wrapper.hidden = !active;
      });
    }

    renderBuyButton(offer, variant) {
      const values = templateValues(offer, variant);
      if (this.idInput) {
        this.idInput.value = variant.id;
        this.idInput.disabled = !variant.available;
      }
      if (this.submitButton) this.submitButton.disabled = !variant.available;

      const label = variant.available
        ? renderTemplate(offer.templates.buttonLabel, values) || window.variantStrings.addToCart
        : window.variantStrings.soldOut;
      this.setText('[data-buy-label]', label);
      this.setText('[data-buy-price]', variant.price);
      this.setCompare('[data-buy-compare]', variant.compareAtPrice);

      const note = this.querySelector('[data-buy-note]');
      if (note) {
        note.textContent = renderTemplate(offer.templates.buttonNote, values);
        note.hidden = note.textContent === '';
      }
    }

    setText(selector, text) {
      const element = this.querySelector(selector);
      if (element && element.textContent !== text) element.textContent = text;
    }

    // An empty compare price hides the struck-through price.
    setCompare(selector, price) {
      const group = this.querySelector(selector);
      if (!group) return;
      group.hidden = !price;
      const struck = group.querySelector('s');
      if (struck && struck.textContent !== (price || '')) struck.textContent = price || '';
    }
  }

  if (!customElements.get('heirloom-gallery')) customElements.define('heirloom-gallery', HeirloomGallery);
  if (!customElements.get('product-configurator')) customElements.define('product-configurator', ProductConfigurator);
}

// Node imports this file for tests. Define the elements only in a browser.
if (typeof window !== 'undefined' && 'customElements' in window) defineElements();
